import { useCallback } from "react";
import { HTTPError } from "ky";
import { toast } from "@/lib/toast";
import apiClient from "@/lib/api-client";
import { getAuthToken } from "@/lib/auth";
import { streamSSE } from "@/lib/sse-client";
import { useSeedDiscoveryStore, type SeedCandidate } from "@/stores/seed-discovery-store";
import { trackEvent } from "@/lib/engagement";
import type { DiscoveryTarget } from "@/types/concept";

interface StartResponse {
  job_id?: string;
  cached_candidates?: SeedCandidate[];
}

/** Store targetKey for a discovery target; the Add-angle sheet computes the same
 * key to tell whether the store's run is its own. */
export function discoveryTargetKey(t?: DiscoveryTarget): string {
  if (!t) return "concept";
  if ("facet_id" in t) return `facet:${t.facet_id}`;
  const { label, question, search_terms, scope } = t.angle;
  return `angle:${label}\u0000${question}\u0000${search_terms}\u0000${JSON.stringify(scope ?? {})}`;
}

/** Shown when a targeted run 409s; Rediscover (force) supersedes the running one. */
export const ANGLE_RUN_BUSY = "A search for this angle is already running — try Rediscover in a minute";

export function useSeedDiscovery(conceptId: string) {
  // `force === true` only — guards against a click event being passed as the arg
  // when `discover` is wired directly to an onClick handler.
  // `target` (facets 1c-3) discovers for one angle: the run is owned by "angle",
  // so Curate's pill/dialog ignore it, and the sheet shows its own status (no toasts).
  // `opts.quiet`: the caller shows status and errors itself (setup ②), so no
  // toasts — their "use Re-discover" copy names a Curate button setup doesn't have.
  const discover = useCallback(async (force: boolean = false, target?: DiscoveryTarget, opts?: { quiet?: boolean }) => {
    const isForce = force === true;
    const quiet = !!target || !!opts?.quiet;
    trackEvent("feature_use", {
      feature: "seed_discovery",
      action: isForce ? "rerun" : "started",
    });
    const { newRun, start, setPhase, setComplete, setError } =
      useSeedDiscoveryStore.getState();
    // A newer run or a reset() supersedes this one: stop writing (and toasting).
    const run = newRun(target ? "angle" : "concept", discoveryTargetKey(target));
    const stale = () => useSeedDiscoveryStore.getState().runId !== run;

    try {
      const res = await apiClient
        .post(`api/concepts/${conceptId}/discover-seeds`, {
          searchParams: isForce ? { force: "true" } : {},
          ...(target ? { json: target } : {}),
        })
        .json<StartResponse>();
      if (stale()) return;

      // Stable re-open: server returned the previously-saved candidate set.
      if (res.cached_candidates) {
        setComplete(res.cached_candidates);
        if (!quiet) toast.success("Showing your saved seed candidates", {
          description: `${res.cached_candidates.length} candidate${res.cached_candidates.length === 1 ? "" : "s"} — use Re-discover to find new ones`,
        });
        return;
      }

      if (!res.job_id) {
        setError("Seed discovery failed");
        if (!quiet) toast.error("Seed discovery failed");
        return;
      }

      start(res.job_id);

      const baseUrl = import.meta.env.VITE_API_URL ?? "http://localhost:8000";
      const token = await getAuthToken();
      const streamUrl = `${baseUrl}/api/concepts/${conceptId}/discover-seeds/${res.job_id}/stream`;

      for await (const event of streamSSE(streamUrl, {
        Authorization: `Bearer ${token}`,
      })) {
        if (stale()) return; // stop reading; the server finishes its job regardless
        if (event.event === "searching") {
          setPhase("searching");
        } else if (event.event === "suggesting") {
          setPhase("suggesting");
        } else if (event.event === "selecting") {
          setPhase("selecting");
        } else if (event.event === "resolving") {
          const data = JSON.parse(event.data);
          setPhase("resolving", { current: data.resolved, total: data.total });
        } else if (event.event === "analyzing") {
          const data = JSON.parse(event.data);
          setPhase("analyzing", { current: data.fetched, total: data.total });
        } else if (event.event === "complete") {
          const data = JSON.parse(event.data);
          setComplete(data.candidates as SeedCandidate[]);
          if (!quiet) toast.success("Seed papers discovered", {
            description: `Found ${data.candidates.length} candidate${data.candidates.length === 1 ? "" : "s"}`,
          });
          return;
        } else if (event.event === "error") {
          const data = JSON.parse(event.data);
          const msg = data.message || "Seed discovery failed";
          setError(msg);
          if (!quiet) toast.error("Seed discovery failed", { description: msg });
          return;
        }
      }

      // Stream closed without terminal event
      if (stale()) return;
      const { phase } = useSeedDiscoveryStore.getState();
      if (phase !== "complete" && phase !== "error") {
        setError("Connection lost");
      }
    } catch (err) {
      if (stale()) return;
      if (target && err instanceof HTTPError && err.response.status === 409) {
        // The backend registry is keyed by (concept, target): this angle's previous run is still going.
        useSeedDiscoveryStore.getState().setError(ANGLE_RUN_BUSY);
        return;
      }
      if (err instanceof HTTPError && err.response.status === 400) {
        const msg = "Add a research question first";
        useSeedDiscoveryStore.getState().setError(msg);
        if (!quiet) toast.error(msg, { description: "Seed discovery searches using your research question and search terms." });
        return;
      }
      const msg = err instanceof Error ? err.message : "Seed discovery failed";
      useSeedDiscoveryStore.getState().setError(msg);
      if (!quiet) toast.error("Seed discovery failed", { description: msg });
    }
  }, [conceptId]);

  return { discover };
}
